import React, { useEffect, useRef, useState } from 'react';
import {
  getGoogleClientId,
  isGoogleConfigured,
  isLinkedInConfigured,
  loadGoogleIdentityServices,
  startLinkedInSignIn,
} from '@/services/socialAuth';
import './SocialAuthButtons.css';

interface SocialAuthButtonsProps {
  /** Called with the Google ID token (credential) once the user consents. */
  onGoogleCredential: (credential: string) => void;
  /** Disable the LinkedIn button while a submit is in flight. */
  disabled?: boolean;
  /** Where the "or" divider sits relative to the buttons. */
  dividerPosition?: 'top' | 'bottom';
}

/** Google caps its button at 400px; never render it wider than the form column. */
const GOOGLE_MAX_WIDTH = 400;

/**
 * "or" divider plus Continue with Google / Continue with LinkedIn.
 * Google uses the official Identity Services button; LinkedIn starts the
 * authorization-code hop (state is kept in this origin's sessionStorage).
 */
export const SocialAuthButtons: React.FC<SocialAuthButtonsProps> = ({
  onGoogleCredential,
  disabled = false,
  dividerPosition = 'top',
}) => {
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const credentialCallbackRef = useRef(onGoogleCredential);
  useEffect(() => {
    credentialCallbackRef.current = onGoogleCredential;
  }, [onGoogleCredential]);
  const [googleError, setGoogleError] = useState('');

  const googleEnabled = isGoogleConfigured();
  const linkedInEnabled = isLinkedInConfigured();

  useEffect(() => {
    if (!googleEnabled) return undefined;
    let cancelled = false;
    const wrapper = googleButtonRef.current;
    let observer: ResizeObserver | undefined;
    let renderedWidth = 0;
    let initialized = false;

    const renderGoogleButton = (width: number) => {
      if (!wrapper || !window.google?.accounts?.id) return false;
      wrapper.innerHTML = '';
      window.google.accounts.id.renderButton(wrapper, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        // Keeps the Google logo next to the label, like the LinkedIn button.
        logo_alignment: 'center',
        width,
      });
      return true;
    };

    // GSI renders a fixed-width button, so it has to be re-rendered whenever the
    // column changes size — otherwise the button overflows and gets clipped.
    const renderToFit = () => {
      if (!wrapper || !initialized) return;
      const available = Math.round(wrapper.clientWidth);
      if (!available) return;
      const width = Math.min(GOOGLE_MAX_WIDTH, available);
      if (width === renderedWidth) return;
      if (renderGoogleButton(width)) renderedWidth = width;
    };

    loadGoogleIdentityServices()
      .then(() => {
        if (cancelled || !wrapper || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: getGoogleClientId(),
          callback: (response) => {
            if (response.credential) credentialCallbackRef.current(response.credential);
          },
        });
        initialized = true;
        renderToFit();
        if (typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(renderToFit);
          observer.observe(wrapper);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setGoogleError(err instanceof Error ? err.message : 'Google sign-in could not be loaded.');
        }
      });

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (wrapper) {
        wrapper.innerHTML = '';
      }
    };
  }, [googleEnabled]);

  if (!googleEnabled && !linkedInEnabled) return null;

  const divider = (
    <div className="social-auth-divider" role="separator">
      <span>or</span>
    </div>
  );

  return (
    <div className="social-auth">
      {dividerPosition === 'top' && divider}

      <div className="social-auth-row">
        {googleEnabled && (
          <div className="social-auth-google">
            <div ref={googleButtonRef} />
          </div>
        )}

        {linkedInEnabled && (
          <button
            type="button"
            className="social-auth-linkedin"
            onClick={() => startLinkedInSignIn()}
            disabled={disabled}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"
              />
            </svg>
            <span>Continue with LinkedIn</span>
          </button>
        )}
      </div>

      {googleError && (
        <p className="social-auth-error" role="alert">
          {googleError}
        </p>
      )}

      {dividerPosition === 'bottom' && divider}
    </div>
  );
};

export default SocialAuthButtons;
